/**
 * Trail — /trail/:tid. A shared investigation path. Shows the documented
 * journey and an Investigation Summary, then invites the visitor to begin
 * at the first page. Nothing gamified — the depth speaks for itself.
 */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { GitBranch, ArrowRight, Loader2, AlertCircle } from "lucide-react";
import { setInvestigationTitle } from "../investigation/investigationMemory";
import { BranchCard } from "../investigation/InvestigationContinues";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrailBranches = ({ tid }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let dead = false;
    axios.get(`${API}/trails/${tid}/branches`)
      .then((r) => { if (!dead) setData(r.data); })
      .catch(() => { if (!dead) setData({ branches: [], explore_url: "/connections" }); });
    return () => { dead = true; };
  }, [tid]);

  if (!data) return null;

  return (
    <div className="mt-14" data-testid="trail-branches-section">
      <div className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#A68F5E]">
        Where This Trail Could Go Next
      </div>
      <p className="text-[12px] text-[#8A8377] mt-2">
        Continue through documented connections from the final stop.
      </p>
      {data.branches.length === 0 ? (
        <div className="mt-5 border border-dashed p-8 text-center"
             style={{ borderColor: "#2D2A26" }} data-testid="trail-branches-empty">
          <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#8A8377]">
            No further verified branches currently mapped
          </div>
          <div className="text-[12px] text-[#5A554C] mt-2">
            The Archive is still expanding this network.
          </div>
          <Link to={data.explore_url || "/connections"}
                data-testid="trail-branches-explore-btn"
                className="mt-5 inline-flex items-center gap-2 font-mono text-[9px] uppercase
                           tracking-[0.3em] border px-4 py-2 text-[#E4DFD3]
                           hover:border-[#D92D20] hover:text-[#D92D20] transition-colors"
                style={{ borderColor: "#3A3630" }}>
            Explore Related Investigations
          </Link>
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          {data.branches.map((b, i) => (
            <BranchCard key={b.url} branch={b} index={i} testPrefix="trail-branch" />
          ))}
        </div>
      )}
    </div>
  );
};

const SummaryLine = ({ n, label, testid }) => (
  n > 0 ? (
    <li className="flex items-baseline gap-2" data-testid={testid}>
      <span className="text-[#D92D20] text-[11px]">•</span>
      <span className="font-display text-[16px] text-[#E4DFD3]">{n.toLocaleString()}</span>
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8A8377]">{label}</span>
    </li>
  ) : null
);

export default function Trail() {
  const { tid } = useParams();
  const [trail, setTrail] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    document.title = "The Archive — Investigation Trail";
    return () => { document.title = "The Archive"; };
  }, []);

  useEffect(() => {
    let dead = false;
    setStatus("loading");
    axios.get(`${API}/trails/${tid}`)
      .then((r) => { if (!dead) { setTrail(r.data); setStatus("ok"); } })
      .catch(() => { if (!dead) setStatus("error"); });
    return () => { dead = true; };
  }, [tid]);

  useEffect(() => {
    if (trail?.path?.[0]?.title) {
      setInvestigationTitle(`Shared Trail: ${trail.path[0].title}`);
    }
  }, [trail]);

  if (status === "loading") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="trail-loading">
        <Loader2 size={18} className="animate-spin inline text-[#A68F5E]" />
      </div>
    );
  }
  if (status === "error" || !trail) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="trail-not-found">
        <AlertCircle size={24} className="text-[#D92D20] mx-auto mb-4" />
        <div className="font-display text-3xl text-[#E4DFD3] mb-2">Trail not on file</div>
        <Link to="/archive"
              className="inline-flex mt-4 items-center gap-2 px-5 py-2.5 font-mono text-[10px] uppercase
                         tracking-[0.35em] border border-[#D92D20] text-[#E4DFD3]
                         hover:bg-[#D92D20] transition-colors">
          Enter the Archive
        </Link>
      </div>
    );
  }

  const s = trail.summary || {};
  const first = trail.path[0];

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-14" data-testid="trail-page">
      <div className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#D92D20] flex items-center gap-2">
        <GitBranch size={12} /> Shared Investigation
      </div>
      <h1 className="font-display text-4xl sm:text-5xl text-[#E4DFD3] mt-3 leading-tight"
          data-testid="trail-title">
        Investigation: {first?.title}
      </h1>
      <p className="text-sm text-[#A39E93] mt-3 leading-relaxed max-w-xl">
        Someone followed this documented path through The Archive. Every step below is
        backed by published dossiers and verified records — trace it yourself.
      </p>

      {/* Journey */}
      <div className="mt-10">
        <div className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#A68F5E] mb-4">Journey</div>
        <ol data-testid="trail-journey" className="space-y-0">
          {trail.path.map((step, i) => (
            <li key={`${step.url}-${i}`}>
              <Link to={step.url}
                    data-testid={`trail-step-${i}`}
                    className="group inline-flex items-center gap-3 py-1.5">
                <span className="font-mono text-[9px] text-[#5A554C] w-6 text-right">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-display text-[18px] text-[#E4DFD3] group-hover:text-[#D92D20] transition-colors">
                  {step.title}
                </span>
              </Link>
              {i < trail.path.length - 1 && (
                <div className="ml-[30px] text-[#3A3630] text-[13px] leading-none select-none">↓</div>
              )}
            </li>
          ))}
        </ol>
      </div>

      {/* Investigation Summary */}
      <div className="mt-10 border p-6" style={{ borderColor: "#2D2A26", background: "#12110E" }}
           data-testid="trail-summary">
        <div className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#A68F5E] mb-4">
          Investigation Summary
        </div>
        <ul className="space-y-2.5">
          <SummaryLine n={s.connections} label="connections travelled" testid="trail-sum-connections" />
          <SummaryLine n={s.people} label={s.people === 1 ? "person" : "people"} testid="trail-sum-people" />
          <SummaryLine n={s.organizations} label={s.organizations === 1 ? "organization" : "organizations"} testid="trail-sum-orgs" />
          <SummaryLine n={s.operations} label={s.operations === 1 ? "operation" : "operations"} testid="trail-sum-ops" />
          <SummaryLine n={s.dossiers} label={s.dossiers === 1 ? "dossier" : "dossiers"} testid="trail-sum-dossiers" />
          <SummaryLine n={s.evidence_viewed} label="pieces of evidence viewed" testid="trail-sum-evidence" />
          <SummaryLine n={s.primary_sources_available} label="primary sources available" testid="trail-sum-sources" />
        </ul>
      </div>

      {first && (
        <Link to={first.url}
              data-testid="trail-begin-btn"
              className="mt-10 inline-flex items-center gap-2 px-6 py-3 font-mono text-[10px]
                         uppercase tracking-[0.35em] border border-[#D92D20] text-[#E4DFD3]
                         hover:bg-[#D92D20] transition-colors">
          Begin this investigation <ArrowRight size={12} />
        </Link>
      )}

      <TrailBranches tid={tid} />
    </div>
  );
}
